import { useCallback, useEffect, useRef, useState } from "react";
import { SocketEvent } from "../interface/SocketEvents";
import { describeReactionForAnnounce } from "../reactionThrowAssets";
import type {
  Connection,
  ReceiveReactionData,
  ReactionAnnounceData,
  ReactionRateLimitedData,
  TableGiphyPlayedData,
  TableGiphyRateLimitedData,
} from "../sockets-client";
import type { ReactionEvent } from "../components/ReactionOverlay";

export type ReactionMenuTarget = {
  playerUUID: string;
  playerName: string;
  anchorX: number;
  anchorY: number;
};

/**
 * Reactions + table GIPHY listeners for Rummy / Literature (same wire protocol as Addata).
 */
export function useTableSocialSocket(socketConnection: Connection) {
  const seenReactionIdsRef = useRef(new Set<string>());
  const seenGiphyIdsRef = useRef(new Set<string>());
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const giphyCooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [reactionQueue, setReactionQueue] = useState<ReactionEvent[]>([]);
  const [reactionAnnounce, setReactionAnnounce] = useState<string | null>(null);
  const [reactionMenuTarget, setReactionMenuTarget] = useState<ReactionMenuTarget | null>(null);
  const [reactionCooldownMs, setReactionCooldownMs] = useState(0);

  const [giphyQueue, setGiphyQueue] = useState<TableGiphyPlayedData[]>([]);
  const [giphyPickerOpen, setGiphyPickerOpen] = useState(false);
  const [giphyCooldownMs, setGiphyCooldownMs] = useState(0);

  useEffect(() => {
    socketConnection.listenForReceiveReaction((data: ReceiveReactionData) => {
      if (seenReactionIdsRef.current.has(data.eventId)) return;
      seenReactionIdsRef.current.add(data.eventId);
      setReactionQueue((prev) => [
        ...prev,
        { eventId: data.eventId, fromPlayerName: data.fromPlayerName, reactionType: data.reactionType },
      ]);
    });

    socketConnection.listenForReactionAnnounce((data: ReactionAnnounceData) => {
      setReactionAnnounce(
        `${data.fromPlayerName} sent ${describeReactionForAnnounce(data.reactionType)} to ${data.toPlayerName}`
      );
    });

    socketConnection.listenForReactionRateLimited((data: ReactionRateLimitedData) => {
      setReactionCooldownMs(data.retryAfterMs);
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = setTimeout(() => setReactionCooldownMs(0), data.retryAfterMs);
    });

    socketConnection.listenForTableGiphyPlayed((data: TableGiphyPlayedData) => {
      if (seenGiphyIdsRef.current.has(data.eventId)) return;
      seenGiphyIdsRef.current.add(data.eventId);
      setGiphyQueue((q) => [...q, data]);
    });

    socketConnection.listenForTableGiphyRateLimited((data: TableGiphyRateLimitedData) => {
      setGiphyCooldownMs(data.retryAfterMs);
      if (giphyCooldownTimerRef.current) clearTimeout(giphyCooldownTimerRef.current);
      giphyCooldownTimerRef.current = setTimeout(() => setGiphyCooldownMs(0), data.retryAfterMs);
    });

    return () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      if (giphyCooldownTimerRef.current) clearTimeout(giphyCooldownTimerRef.current);
      socketConnection.removeListener(SocketEvent.ReceiveReaction);
      socketConnection.removeListener(SocketEvent.ReactionAnnounce);
      socketConnection.removeListener(SocketEvent.ReactionRateLimited);
      socketConnection.removeListener(SocketEvent.TableGiphyPlayed);
      socketConnection.removeListener(SocketEvent.TableGiphyRateLimited);
    };
  }, [socketConnection]);

  const handleReactionComplete = useCallback((eventId: string) => {
    setReactionQueue((prev) => prev.filter((r) => r.eventId !== eventId));
  }, []);

  const dismissGiphyHead = useCallback(() => {
    setGiphyQueue((q) => q.slice(1));
  }, []);

  const handleReactionSelect = useCallback(
    (type: string) => {
      if (!reactionMenuTarget) return;
      const clientEventId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      socketConnection.sendReaction(reactionMenuTarget.playerUUID, type, clientEventId);
      setReactionMenuTarget(null);
    },
    [reactionMenuTarget, socketConnection]
  );

  const closeReactionMenu = useCallback(() => setReactionMenuTarget(null), []);

  const shareGiphy = useCallback(
    (giphyId: string) => {
      const clientEventId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      socketConnection.shareTableGiphy(giphyId, clientEventId);
      setGiphyPickerOpen(false);
    },
    [socketConnection]
  );

  return {
    reactionQueue,
    reactionAnnounce,
    setReactionAnnounce,
    reactionMenuTarget,
    setReactionMenuTarget,
    reactionCooldownMs,
    handleReactionSelect,
    closeReactionMenu,
    handleReactionComplete,
    giphyQueue,
    dismissGiphyHead,
    giphyPickerOpen,
    setGiphyPickerOpen,
    shareGiphy,
    giphyCooldownMs,
  };
}
